import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import { ArrowRightUniversal } from '@duxton-web/icons';
import {
  Alert,
  Button,
  Checkbox,
  Form,
  Input,
  Modal,
  ModalProps,
  notification,
  Select,
  Typography,
} from '@duxton-web/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

import { Experiment, RegionalExperimentConfigValues } from '../../domain';
import { getExperimentByCountryCode } from '../../utils';
import { AccessControlWrapper } from '@/common/components';
import { getCountryNameByCountryCode } from '@/common/CountryCodesMap';
import { getDomainFamily } from '@/common/domains';
import {
  ABORTED,
  HTTP_STATUS,
  REQUESTED,
  RUNNING,
  SCHEDULED,
  STARTED,
} from '@/constants';
import {
  useAbortExperiment,
  useAbortRegionalExperiment,
  useGetExperiment,
  useGetRegionalExperimentChildren,
} from '@/hooks';
import { AdaptiveExperiment } from '@/lib/PlannerDTOs';
import { AccessTargetInput, UserAction } from '@/utils/accessControl';
import { useAppNavigate } from '@/utils/navigation';
import { formatHasAccessErrorGeneric } from '@/utils/url';

interface AbortReasonOption {
  value: string;
  label: string;
  requiresText?: boolean;
}

interface AbortCategory {
  value: string;
  payloadValue: string;
  label: string;
  helper: string;
  reasons: AbortReasonOption[];
}

interface AbortReasonPayload {
  abortReason: string;
  detailedReason: string;
  evidence?: string;
  additionalContext?: string;
}

const abortCategories: AbortCategory[] = [
  {
    value: 'decision',
    payloadValue: 'reachedADecisionEarly',
    label: 'Reached a decision early',
    helper:
      'Use this when the team already has enough evidence to stop the experiment and explicitly record the decision.',
    reasons: [
      {
        value: 'enoughData',
        label: 'We already have enough data to make the decision',
      },
      {
        value: 'otherDecision',
        label: 'Other reason',
        requiresText: true,
      },
    ],
  },
  {
    value: 'defect',
    payloadValue: 'defectiveOrBrokenExperiment',
    label: 'Defective or broken experiment',
    helper:
      'Use this when the setup, instrumentation, or live behavior is invalid and the run should not create a decision-ready report.',
    reasons: [
      {
        value: 'prodIssue',
        label:
          'The experiment caused an issue in production (CE tickets, MEX unhappy, feature not rendering, or similar)',
      },
      {
        value: 'wrongConstraints',
        label:
          'The experiment was launched with the wrong constraints, city, or audience',
      },
      {
        value: 'notWorking',
        label: 'The experiment configuration did not work as expected',
      },
      {
        value: 'facetNotInstrumented',
        label: 'One or more required facets or events were not instrumented',
      },
      { value: 'otherOperational', label: 'Other reason', requiresText: true },
    ],
  },
  {
    value: 'test',
    payloadValue: 'testOrSlotReservationExperiment',
    label: 'Test or slot-reservation experiment',
    helper:
      'Use this when the run was only used to test setup or reserve traffic and should be cleared with audit context.',
    reasons: [
      {
        value: 'placeholder',
        label:
          'This experiment was created only to block a traffic slot for a future run',
      },
      {
        value: 'testing',
        label:
          'This was a test experiment and was never intended for a real decision',
      },
      { value: 'otherTest', label: 'Other reason', requiresText: true },
    ],
  },
];

const buildAbortReason = ({
  category,
  selectedReason,
  evidence,
  additionalContext,
}: {
  category?: AbortCategory;
  selectedReason?: string;
  evidence?: string;
  additionalContext?: string;
}) => {
  const reasonPayload: AbortReasonPayload = {
    abortReason: category?.payloadValue || '',
    detailedReason: selectedReason || '',
  };

  const trimmedEvidence = evidence?.trim();
  const trimmedAdditionalContext = additionalContext?.trim();

  if (trimmedEvidence) {
    reasonPayload.evidence = trimmedEvidence;
  }

  if (trimmedAdditionalContext) {
    reasonPayload.additionalContext = trimmedAdditionalContext;
  }

  return JSON.stringify(reasonPayload);
};

const AbortModal = ({
  experiment,
  adaptiveExperiment,
  open,
  onCancel,
  onOk,
}: ModalProps & {
  experiment?: Experiment;
  adaptiveExperiment?: AdaptiveExperiment;
}) => {
  const params = useParams<{ experimentName: string }>();
  const { experimentName } = params;

  const { navigateTo } = useAppNavigate();

  const mainExperiment = experiment || adaptiveExperiment;
  const isAdaptiveExp = !experiment && !!adaptiveExperiment;
  const isRegionalExperiment = !!mainExperiment?.['regionalExperimentName'];
  const experimentState = mainExperiment?.state;
  const canAbortAsWhole =
    (isRegionalExperiment && experimentState === REQUESTED) ||
    !isRegionalExperiment;

  const [configurations, setConfigurations] = useState<
    RegionalExperimentConfigValues[]
  >([]);

  const [valid, setIsValid] = useState(false);

  const [form] = Form.useForm();
  const abortCategory = Form.useWatch('category', { form, preserve: true });
  const abortDetailedReason = Form.useWatch('detailed_reason', {
    form,
    preserve: true,
  });
  const selectedAbortCategory = abortCategories.find(
    (category) => category.value === abortCategory,
  );
  const selectedDetailedReason = selectedAbortCategory?.reasons.find(
    (reason) => reason.value === abortDetailedReason,
  );
  const isDecisionAbort = selectedAbortCategory?.value === 'decision';
  const isAdditionalReasonRequired =
    selectedDetailedReason?.requiresText || false;
  const shouldShowAdditionalContext = !!selectedDetailedReason;

  const { data: regionalExperiment, refetch } = useGetExperiment(
    mainExperiment?.name,
    {
      enabled: false,
    },
  );

  const queryClient = useQueryClient();

  useEffect(() => {
    if (open && !experimentName) {
      refetch();
    }
  }, [open]);

  useEffect(() => {
    setConfigurations(
      regionalExperiment?.regional_experiment_config_values || [],
    );
  }, [regionalExperiment]);

  const { mutate, isPending } = useAbortExperiment({
    onSuccess: () => {
      notification.success({
        message: 'Aborting an Experiment',
        description: 'Experiment was successfully aborted.',
      });
      setTimeout(() => {
        onOk(null);
        navigateTo(`/${getDomainFamily()}/experiments`);
      }, 0);
    },
    onError: (error) => {
      let description = (
        <span>
          Experiment request was not aborted, due to: {error.toString()}
        </span>
      );
      if (error.response && error.response.status === HTTP_STATUS.FORBIDDEN) {
        description = formatHasAccessErrorGeneric(error);
      }
      notification.error({
        description,
        message: 'Aborting an Experiment',
      });
    },
  });

  const {
    mutate: mutateRegionalExperiment,
    isPending: isPendingRegionalExperiment,
  } = useAbortRegionalExperiment({
    onSuccess: () => {
      notification.success({
        message: 'Aborting an Experiment',
        description: 'Experiment was successfully aborted.',
      });
      setTimeout(() => {
        onOk(null);

        queryClient.invalidateQueries({
          queryKey: ['planner', 'regionalExperimentChildren', experimentName],
        });
      }, 0);
    },
    onError: (error) => {
      let description = (
        <span>
          Experiment request was not aborted, due to: {error.toString()}
        </span>
      );
      if (error.response && error.response.status === HTTP_STATUS.FORBIDDEN) {
        description = formatHasAccessErrorGeneric(error);
      }
      notification.error({
        description,
        message: 'Aborting an Experiment',
      });
    },
  });

  const {
    data: regionalExperimentChildren,
    isLoading: isLoadingRegionalExperimentChildren,
  } = useGetRegionalExperimentChildren(
    mainExperiment?.['regionalExperimentName'],
    '',
    {
      enabled: !canAbortAsWhole,
    },
  );

  useEffect(() => {
    form.setFieldsValue({
      country_code: '',
      category: '',
      detailed_reason: '',
      evidence: '',
      additional_reason: '',
      acknowledge: false,
    });
    setIsValid(false);
  }, [open]);

  useEffect(() => {
    if (!open) return;

    form.setFieldsValue({
      detailed_reason: '',
      evidence: '',
      additional_reason: '',
    });
    setIsValid(false);
  }, [abortCategory, open]);

  const validateForm = useCallback(() => {
    const {
      country_code: countryCode,
      category,
      detailed_reason: detailedReason,
      evidence,
      additional_reason: additionalReason,
      acknowledge,
    } = form.getFieldsValue();

    const isRegionalAbortValid = canAbortAsWhole
      ? true
      : !!countryCode && !!acknowledge;
    const isEvidenceValid = !isDecisionAbort || !!evidence?.trim();
    const isAdditionalReasonValid =
      !isAdditionalReasonRequired || !!additionalReason?.trim();

    setIsValid(
      !!category &&
        !!detailedReason &&
        isRegionalAbortValid &&
        isEvidenceValid &&
        isAdditionalReasonValid,
    );
  }, [canAbortAsWhole, form, isAdditionalReasonRequired, isDecisionAbort]);

  useEffect(() => {
    if (!open) return;

    validateForm();
  }, [isAdditionalReasonRequired, isDecisionAbort, open, validateForm]);

  const handleFormValuesChange = useCallback(
    (_changedValues: any, _allFields: any) => {
      validateForm();
    },
    [validateForm],
  );

  if (!open) return null;

  return (
    <Modal
      title={
        <div>
          <Typography.Title ellipsis level={3} style={{ marginBottom: 8 }}>
            Abort {mainExperiment?.name}?
          </Typography.Title>
          <Typography.Text type="secondary">
            Once an experiment is aborted it cannot be revived. If the team
            needs to test again, a new experiment should be created and linked
            back to this record.
          </Typography.Text>
        </div>
      }
      open
      onCancel={onCancel}
      onOk={() => {
        const reason = buildAbortReason({
          category: selectedAbortCategory,
          selectedReason: form.getFieldValue('detailed_reason'),
          evidence: form.getFieldValue('evidence'),
          additionalContext: form.getFieldValue('additional_reason'),
        });

        if (canAbortAsWhole) {
          mutate({
            expName: mainExperiment?.name,
            action: 'cancel',
            reason,
            isAdaptiveExp: isAdaptiveExp,
          });
        } else {
          mutateRegionalExperiment({
            expName: mainExperiment?.name,
            countryCode: form.getFieldValue('country_code'),
            action: 'cancel',
            reason,
            isAdaptiveExp: isAdaptiveExp,
          });
        }
      }}
      okText="Abort experiment"
      okButtonProps={{
        size: 'small',
        danger: true,
        disabled: !valid || isPending || isPendingRegionalExperiment,
        loading: isPending || isPendingRegionalExperiment,
      }}
      cancelButtonProps={{
        size: 'small',
        loading: isPending || isPendingRegionalExperiment,
        disabled: isPending || isPendingRegionalExperiment,
      }}
      width={760}
      centered
    >
      <Form
        form={form}
        // onFinish={onOk}
        initialValues={{
          country_code: '',
          category: '',
          detailed_reason: '',
          evidence: '',
          additional_reason: '',
          acknowledge: false,
        }}
        layout="vertical"
        onValuesChange={handleFormValuesChange}
        validateTrigger="onBlur"
        style={{ marginTop: 24 }}
      >
        {!canAbortAsWhole && (
          <Form.Item
            name="country_code"
            label="Country"
            required={true}
            rules={[{ required: true, message: 'Please select a country' }]}
          >
            <Select
              loading={isLoadingRegionalExperimentChildren}
              options={configurations
                .filter((c: RegionalExperimentConfigValues) => {
                  const experiment = getExperimentByCountryCode(
                    (regionalExperimentChildren as any) || [],
                    c.country_code,
                  );
                  return experiment?.state !== ABORTED;
                })
                .map((c: RegionalExperimentConfigValues) => {
                  return {
                    value: c.country_code,
                    label: getCountryNameByCountryCode(c.country_code),
                  };
                })}
            />
          </Form.Item>
        )}

        <Form.Item
          name="category"
          label="What kind of abort is this?"
          required={true}
          rules={[{ required: true, message: 'Please select an abort type' }]}
        >
          <Select
            placeholder="Select a category"
            options={abortCategories.map((category) => {
              return {
                value: category.value,
                label: category.label,
              };
            })}
          />
        </Form.Item>

        <Typography.Text
          type="secondary"
          style={{ display: 'block', marginTop: -16, marginBottom: 24 }}
        >
          {selectedAbortCategory?.helper ||
            'Choose the scenario that best explains why this experiment is being stopped.'}
        </Typography.Text>

        <Form.Item
          name="detailed_reason"
          label="Select the detailed reason"
          required={true}
          rules={[
            {
              required: true,
              message: 'Please select a detailed reason',
            },
          ]}
        >
          <Select
            disabled={!selectedAbortCategory}
            placeholder={
              selectedAbortCategory
                ? 'Select a detailed reason'
                : 'Select an abort type first'
            }
            options={(selectedAbortCategory?.reasons || []).map((reason) => {
              return {
                value: reason.value,
                label: reason.label,
              };
            })}
          />
        </Form.Item>

        {isDecisionAbort && (
          <Form.Item
            name="evidence"
            label="What evidence supports ending this experiment now?"
            required={true}
            rules={[
              {
                required: true,
                whitespace: true,
                message: 'Please capture the evidence for this decision',
              },
            ]}
          >
            <Input.TextArea
              rows={5}
              placeholder="Example: The primary metric has a clear positive read, guardrails are stable, and PA review confirms we can decide before the planned end date."
            />
          </Form.Item>
        )}

        {isDecisionAbort && (
          <Typography.Text
            type="secondary"
            style={{ display: 'block', marginTop: -16, marginBottom: 24 }}
          >
            Capture the rationale so the learning record can stand on its own.
          </Typography.Text>
        )}

        {shouldShowAdditionalContext && (
          <Form.Item
            name="additional_reason"
            label="Additional context"
            preserve={false}
            required={isAdditionalReasonRequired}
            rules={[
              () => ({
                validator(_, value) {
                  const trimmedValue = value?.trim();
                  if (!trimmedValue) {
                    if (isAdditionalReasonRequired) {
                      return Promise.reject(
                        new Error('Please provide additional context'),
                      );
                    }
                    return Promise.resolve();
                  }
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <Input.TextArea
              rows={5}
              placeholder="Add any extra context for this abort."
            />
          </Form.Item>
        )}

        {!canAbortAsWhole && (
          <Alert
            style={{ marginTop: 24 }}
            message={
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Form.Item
                  name="acknowledge"
                  required={true}
                  valuePropName="checked"
                  style={{ marginBottom: 0 }}
                  rules={[
                    () => ({
                      validator(_, value) {
                        if (value) {
                          return Promise.resolve();
                        }
                        return Promise.reject();
                      },
                    }),
                  ]}
                >
                  <Checkbox />
                </Form.Item>
                I understand I am aborting an Experiment and have checked with
                developers that code reference to this Experiment has been
                removed
              </div>
            }
          />
        )}
      </Form>
    </Modal>
  );
};

const Abort = ({
  variableTechFamilies,
  experiment,
  adaptiveExperiment,
  ghost,
  icon,
  accessTargets,
}: {
  variableTechFamilies: string[];
  experiment: Experiment;
  adaptiveExperiment: AdaptiveExperiment;
  ghost?: boolean;
  icon?: ReactNode;
  accessTargets?: AccessTargetInput[];
}) => {
  const mainExperiment = experiment || adaptiveExperiment;

  const [open, setOpen] = useState(false);

  if (![REQUESTED, SCHEDULED, RUNNING, STARTED].includes(mainExperiment?.state))
    return null;

  return (
    <>
      <AccessControlWrapper
        action={UserAction.ABORT_CURRENT_EXPERIMENT}
        techFamilies={variableTechFamilies}
        accessTargets={accessTargets}
      >
        {(hasAccess: boolean) => (
          <Button
            size="small"
            shape="round"
            danger={!ghost}
            ghost={ghost}
            onClick={() => {
              setOpen(true);
            }}
            disabled={!hasAccess}
            icon={icon}
          >
            Abort {icon ? null : <ArrowRightUniversal />}
          </Button>
        )}
      </AccessControlWrapper>
      <AbortModal
        experiment={experiment}
        adaptiveExperiment={adaptiveExperiment}
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => {
          setOpen(false);
        }}
      />
    </>
  );
};

export default Abort;
